import React, { useEffect, useCallback, useMemo, useRef, useState } from 'react';
import useScheduleTable from '../RoomScheduleTable/useScheduleTable';
import { IMeetingOrderFn, IMeetingOrderForm } from 'type/meeting';
import { IMeetingDetail } from 'type/meetingDetail';
import { Contact as ContactApi } from 'api';
import type { V2MeetingRecommendForm } from 'api/recommend';
import { Contact, Person } from 'type/contact';
import { cloneDeep } from 'lodash-es';
import styles from './index.module.scss';
import UploadSDK from 'lib/upload';
import { TaskOptions } from '@isp/upload-sdk/dist/types/type';
import { useSelector } from 'react-redux';
import { RootState } from 'model';
import { includeMasterInMeetingMembers } from 'util/meeting';
import { getStaffId } from 'data/storage';
import {
  bindMeetingOrderPointHub,
  createMeetingOrderPointHubBridge,
  getMeetingOrderPointHubComponentOptions
} from 'lib/pointHub';
import type { MeetingOrderPointHubElement } from 'lib/pointHub';

interface IProps {
  /** 标题 */
  title: string;
  /** 小标题 */
  subTitle?: string;
  /** 默认表单参数 */
  formValue?: Partial<IMeetingOrderForm> | null;
  /** 会议详情 */
  meetingDetail?: IMeetingDetail | null;
  /** 确认按钮文案 */
  confirmText?: string;
  /** 表单类型 */
  formType: 'order' | 'edit';
  /** 是否启用组件内部 AI 推荐 */
  aiRecommendEnabled?: boolean;
  /** 首次推荐请求参数覆盖 */
  initialRecommendParams?: Partial<V2MeetingRecommendForm>;
  initialRecommendRequestKey?: string;
  onConfirm: IMeetingOrderFn;
  onCancel: () => void;
}

interface MeetingOrderFormRef extends HTMLElement, MeetingOrderPointHubElement {
  formValue?: Partial<IMeetingOrderForm>;
  formType?: 'order' | 'edit';
  meetingDetail?: IMeetingDetail;
  excludeMasterFromMembers?: boolean;
  uploadFile: (option: TaskOptions) => { cancelTask: () => void };
}

type FormMember = IMeetingOrderForm['members'][number];
type CurrentMemberSource = Partial<Omit<Person, 'person'> & Omit<Contact, 'person'>> & {
  customJobTitle?: string;
  person?: Partial<Person> | string;
};

const getMemberKey = (member?: Partial<FormMember>) => {
  return member?.refId || member?.userId || member?.id;
};

const toFormMember = (source?: CurrentMemberSource | null): FormMember | undefined => {
  if (!source) return undefined;

  const person = typeof source.person === 'object' ? source.person : undefined;
  const personId = typeof source.person === 'string' ? source.person : undefined;
  const refId = source.refId || source.staffId || source.personStaffId || person?.refId || person?.staffId || person?.personStaffId || getStaffId();
  const memberName = source.memberName || source.displayName || source.realName || source.realname || source.name || person?.memberName || person?.displayName || person?.realName || person?.realname || person?.name;

  if (!refId || !memberName) return undefined;

  return {
    id: source.id || source._id || source.userId || person?.id || person?._id || person?.userId || personId || refId,
    refId,
    userId: source.userId || person?.userId || person?.id || personId || '',
    memberName,
    teamId: source.orgId || person?.orgId || '',
    teamName: source.teamName || source.department || source.orgName || person?.teamName || person?.department || person?.orgName || '',
    teamQualifiedName: source.teamQualifiedName || source.department || source.orgName || person?.teamQualifiedName || person?.department || person?.orgName || '',
    memberNamePinyin: '',
    memberNameShortPinyin: '',
    mobile: source.mobile || person?.mobile || '',
    jobTitle: source.jobTitle || source.customJobTitle || person?.jobTitle || '',
    cardPhoto: source.cardPhoto || source.avatar || person?.cardPhoto || person?.avatar || ''
  };
};

const MeetingOrderForm: React.FC<IProps> = ({ title, subTitle, formType, formValue, meetingDetail, aiRecommendEnabled = true, initialRecommendParams, initialRecommendRequestKey, onConfirm, onCancel, confirmText }) => {
  const { componentRef, earlyScheduleDay } = useScheduleTable<MeetingOrderFormRef>({
    aiRecommendEnabled: false,
    initialRecommendParams,
    initialRecommendRequestKey,
    syncCheckedAreasAfterRoomDialogOpened: true
  });
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const pointHubUserRef = useRef(userInfo);
  pointHubUserRef.current = userInfo;
  const pointHubContext = useMemo(() => ({ getUser: () => pointHubUserRef.current }), []);
  const pointHubCollect = useMemo(
    () => createMeetingOrderPointHubBridge(pointHubContext),
    [pointHubContext]
  );
  const orderSettings = useSelector((state: RootState) => state.meeting.orderSettings);
  const [currentUserMember, setCurrentUserMember] = useState<FormMember>();
  const [currentUserMemberReady, setCurrentUserMemberReady] = useState(formType !== 'order');

  useEffect(() => {
    if (formType !== 'order') {
      setCurrentUserMemberReady(true);
      return;
    }

    const userMember = toFormMember(userInfo);
    if (userMember) {
      setCurrentUserMember((prev) => (getMemberKey(prev) === getMemberKey(userMember) ? prev : userMember));
      setCurrentUserMemberReady(true);
      return;
    }

    const staffId = userInfo?.staffId || getStaffId();
    if (!staffId) {
      setCurrentUserMemberReady(true);
      return;
    }

    let disposed = false;
    setCurrentUserMemberReady(false);
    ContactApi.getDetailByStaffIds(staffId)
      .toPromise()
      .then((res) => {
        if (disposed) return;
        const contactMember = toFormMember(res?.data?.[0]);
        if (contactMember) {
          setCurrentUserMember((prev) => (getMemberKey(prev) === getMemberKey(contactMember) ? prev : contactMember));
        }
        setCurrentUserMemberReady(true);
      })
      .catch((err) => {
        console.warn('获取当前预约人信息失败', err);
        if (!disposed) setCurrentUserMemberReady(true);
      });

    return () => {
      disposed = true;
    };
  }, [formType, userInfo]);

  const includeMasterInFormMembers = useCallback(
    (value: Partial<IMeetingOrderForm> | null | undefined, fallbackMaster?: IMeetingOrderForm['members'][number]) => {
      const nextValue = cloneDeep(value || {});
      const master = nextValue.master?.length ? nextValue.master : fallbackMaster ? [fallbackMaster] : [];
      const members = nextValue.members ? [...nextValue.members] : [];

      master.forEach((masterItem) => {
        const masterKey = getMemberKey(masterItem);
        if (!masterKey) return;
        const hasMaster = members.some((member) => getMemberKey(member) === masterKey);
        if (!hasMaster) members.unshift(masterItem);
      });

      return {
        ...nextValue,
        master,
        members
      };
    },
    []
  );

  const stableFormValue = useMemo<Partial<IMeetingOrderForm> | undefined>(() => {
    if (formType === 'order') {
      if (!currentUserMemberReady) return undefined;
      if (!formValue && !currentUserMember) return undefined;
      return includeMasterInFormMembers(formValue, currentUserMember);
    }

    if (!formValue) return undefined;
    return includeMasterInFormMembers(formValue);
  }, [formType, formValue, currentUserMember, currentUserMemberReady, includeMasterInFormMembers]);

  const onUploadFile = (fileOption: TaskOptions) => {
    return UploadSDK.addTask(fileOption);
  };

  useEffect(() => {
    const element = componentRef.current;
    if (!element) return;
    const options = getMeetingOrderPointHubComponentOptions(pointHubContext);
    return bindMeetingOrderPointHub(element, pointHubCollect, {
      ...options,
      enabled: formType === 'order' && options.enabled
    });
  }, [componentRef, formType, pointHubCollect, pointHubContext, userInfo]);

  /** 创建会议下 —— 设置表单默认值 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.excludeMasterFromMembers = false;
    if (formType === 'order') componentRef.current.aiRecommendEnabled = false;
    if (stableFormValue) componentRef.current.formValue = cloneDeep(stableFormValue);
    if (formType !== 'order' || !aiRecommendEnabled || !currentUserMemberReady || !orderSettings) return;

    const timer = setTimeout(() => {
      if (componentRef.current) componentRef.current.aiRecommendEnabled = true;
    }, 0);
    return () => clearTimeout(timer);
  }, [formType, stableFormValue, aiRecommendEnabled, currentUserMemberReady, orderSettings]);

  /** 编辑模式 —— 会议详情数据转换成表单值 */
  useEffect(() => {
    if (!componentRef.current || !meetingDetail) return;
    componentRef.current.excludeMasterFromMembers = false;
    componentRef.current.meetingDetail = cloneDeep(meetingDetail);
  }, [meetingDetail]);

  // 检查是否有会议地点
  const hasMeetingLocation = useCallback((meeting: any) => {
    const roomBookingForms = meeting?.roomBookingForms || [];
    const roomId = meeting?.roomId;
    const address = meeting?.address;

    // 如果有会议室预约表单、会议室ID或地址，则认为有会议地点
    return (roomBookingForms.length > 0) || !!roomId || !!address;
  }, []);

  // 检查会议时间是否在可预约范围内
  const checkTimeInRange = useCallback((meeting: IMeetingOrderForm) => {
    // 只对预约会议进行时间范围检查（repeatMeetingType 为 null 或 0 表示预约会议）
    const meetingAny = meeting as any;
    const repeatMeetingType = meetingAny.repeatMeetingType;
    if (repeatMeetingType !== null && repeatMeetingType !== 0 && repeatMeetingType !== undefined) {
      console.log('非预约会议，跳过时间范围检查');
      return true;
    }

    // earlyScheduleDay 为 0 表示不限制
    if (earlyScheduleDay === 0) {
      console.log('earlyScheduleDay 为 0，不限制时间范围');
      return true;
    }

    // 检查会议开始时间是否在范围内
    const beginTime = meetingAny.beginTime || meetingAny.startTime;
    if (!beginTime) {
      console.log('会议开始时间未设置');
      return true; // 如果没有设置开始时间，允许继续（会有其他验证）
    }

    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const maxDate = todayStart + (earlyScheduleDay - 1) * 24 * 3600 * 1000; // 最大可选日期的 00:00
    const beginDate = new Date(beginTime);
    const beginDateOnly = new Date(beginDate.getFullYear(), beginDate.getMonth(), beginDate.getDate()).getTime();
    const isInRange = beginDateOnly <= maxDate;

    console.log('检查会议时间范围：', {
      earlyScheduleDay,
      todayStart: new Date(todayStart).toISOString(),
      maxDate: new Date(maxDate).toISOString(),
      beginTime: new Date(beginTime).toISOString(),
      beginDateOnly: new Date(beginDateOnly).toISOString(),
      isInRange
    });

    return isInRange;
  }, [earlyScheduleDay]);

  const onConfirmDone: IMeetingOrderFn = useCallback((meeting: any) => {
    console.log('onConfirmDone MeetingOrderForm ===>', meeting);
    const meetingWithMaster = includeMasterInMeetingMembers(meeting);

    // 如果没有预约会议地点，不进行时间范围检查，允许正常预约
    if (!hasMeetingLocation(meetingWithMaster)) {
      console.log('没有预约会议地点，跳过时间范围检查');
      return onConfirm(meetingWithMaster);
    }

    // 检查预约会议的时间是否在可预约范围内
    if (!checkTimeInRange(meetingWithMaster)) {
      return new Promise((resolve) => {
        resolve({ success: false, errMesaage: '会议时间超出可预约时间范围' });
      });
    }
    // 时间范围检查通过，调用传入的 onConfirm
    return onConfirm(meetingWithMaster);
  }, [hasMeetingLocation, checkTimeInRange, onConfirm]);

  /** 属性设置 */
  useEffect(() => {
    if (!componentRef.current) return;
    componentRef.current.confirm = onConfirmDone;
    componentRef.current.cancel = onCancel;
    componentRef.current.uploadFile = onUploadFile;
    componentRef.current.filterPlaceholder = '搜索成员、部门、最近会议';
    componentRef.current.nodeOrder = 'teamFirst';
    componentRef.current.excludeMasterFromMembers = false;
  }, [onConfirmDone, onCancel]);

  useEffect(() => {
    if (!componentRef.current) return;
    console.log('earlyScheduleDay MeetingOrderForm ===>', earlyScheduleDay);
    componentRef.current.disabledSubsequentDays = earlyScheduleDay;
  }, [earlyScheduleDay]);

  return (
    <>
      <div className={styles.MeetingOrderForm}>
        <div className={styles.titleWrapper}>
          <span className={styles.title}>{title}</span>
          {subTitle && (
            <>
              <span className={styles.separator}> | </span>
              <span className={styles.subTitle}> {subTitle}</span>
            </>
          )}
        </div>
        <ce-meeting-order theme="client" form-type={formType} confirm-text={confirmText} ref={componentRef} />
      </div>
    </>
  );
};

export default MeetingOrderForm;
